import React, { useState, useEffect } from "react";
import { X, Check, Save } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";

interface Props {
  user: any;
  onClose: () => void;
  onSuccess: () => void;
}

const EditTeamMemberModal: React.FC<Props> = ({ user, onClose, onSuccess }) => {
  const { role: myRole, token } = useAdminAuth();
  
  // Selections
  const [selectedRole, setSelectedRole] = useState(user.role); // Pre-fill current role
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedTL, setSelectedTL] = useState("");
  
  // Dropdown Data
  const [admins, setAdmins] = useState([]);
  const [managers, setManagers] = useState([]);
  const [tls, setTls] = useState([]);

  const [loading, setLoading] = useState(false);

  // Roles Logic
  const allowedRoles = myRole === "SUPER_ADMIN" 
    ? ["ADMIN", "MANAGER", "TEAM_LEADER", "AGENT"]
    : ["MANAGER", "TEAM_LEADER", "AGENT"];

  // --- FETCH HIERARCHY LOGIC (Cascading Dropdowns) ---
  const fetchHierarchy = async (roleType: string, parentId: string | null, setState: any) => {
    let url = `https://luxuriyahomes.com/api/admin/team/hierarchy?role=${roleType}`;
    if (parentId) url += `&parentId=${parentId}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setState(await res.json());
  };

  useEffect(() => {
    if (myRole === "SUPER_ADMIN") fetchHierarchy("ADMIN", null, setAdmins);
  }, []);

  useEffect(() => {
    if (selectedAdmin || myRole === "ADMIN") {
        fetchHierarchy("MANAGER", selectedAdmin || null, setManagers);
    }
  }, [selectedAdmin]);

  useEffect(() => {
    if (selectedManager) fetchHierarchy("TEAM_LEADER", selectedManager, setTls);
  }, [selectedManager]);


  // --- SUBMIT LOGIC ---
  const handleUpdate = async () => {
    // Decide Parent ID based on Role
    let newParentId = null;
    if (selectedRole === "MANAGER") newParentId = selectedAdmin ? Number(selectedAdmin) : null;
    else if (selectedRole === "TEAM_LEADER") newParentId = selectedManager ? Number(selectedManager) : null;
    else if (selectedRole === "AGENT") newParentId = selectedTL ? Number(selectedTL) : null;

    setLoading(true);
    try {
        const res = await fetch("https://luxuriyahomes.com/api/admin/team/update-member", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ 
                userId: user.id, 
                newRole: selectedRole,
                newParentId: newParentId 
            })
        });

        if(res.ok) {
            onSuccess();
            onClose();
        } else {
            alert("Error updating member");
        }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] backdrop-blur-sm p-4 font-[Serif]">
      <div className="bg-[#1c1c1c] w-full max-w-lg rounded-2xl border border-zinc-800 p-8 shadow-2xl relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={20}/></button>
        
        <h2 className="text-2xl text-white font-light mb-1">Edit Team Member</h2>
        <p className="text-zinc-500 text-sm mb-6">Updating: <span className="text-amber-500 font-bold">{user.name}</span></p>

        <div className="space-y-4">
          {/* Role Selection */}
          <div>
            <label className="text-xs uppercase text-zinc-500 font-bold tracking-wider">Change Role</label>
            <select 
              className="w-full mt-2 bg-black border border-zinc-800 text-white p-3 rounded focus:border-amber-500 outline-none"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              {allowedRoles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          {/* HIERARCHY SELECTION (Who is the new Boss?) */}
          <div className="p-4 bg-zinc-900/50 rounded border border-zinc-800 space-y-3">
             <p className="text-xs text-amber-500 uppercase font-bold">Reassign Parent (Transfer)</p>
             
             {/* Admin Select (Only for Super Admin managing Managers+) */}
             {myRole === "SUPER_ADMIN" && ["MANAGER", "TEAM_LEADER", "AGENT"].includes(selectedRole) && (
                <select className="w-full bg-black border border-zinc-700 text-white p-2 rounded text-sm" onChange={e => setSelectedAdmin(e.target.value)}>
                    <option value="">Select New Admin Parent</option>
                    {admins.map((a:any) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
             )}

             {/* Manager Select */}
             {["TEAM_LEADER", "AGENT"].includes(selectedRole) && (
                <select className="w-full bg-black border border-zinc-700 text-white p-2 rounded text-sm" onChange={e => setSelectedManager(e.target.value)}>
                    <option value="">Select New Manager Parent</option>
                    {managers.map((m:any) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
             )}

             {/* TL Select */}
             {selectedRole === "AGENT" && (
                <select className="w-full bg-black border border-zinc-700 text-white p-2 rounded text-sm" onChange={e => setSelectedTL(e.target.value)}>
                    <option value="">Select New TL Parent</option>
                    {tls.map((t:any) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
             )}
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-4">
            <button onClick={onClose} className="text-zinc-400 hover:text-white text-sm uppercase tracking-wider font-bold">Cancel</button>
            <button 
                onClick={handleUpdate}
                disabled={loading}
                className="bg-amber-600 hover:bg-amber-500 text-black px-6 py-3 rounded font-bold uppercase tracking-widest text-xs flex items-center gap-2"
            >
                {loading ? "Saving..." : "Save Changes"} <Save size={16}/>
            </button>
        </div>
      </div>
    </div>
  );
};

export default EditTeamMemberModal;